import { apiFetch } from '../lib/http';
import { asPage, qs } from '../lib/paginate';
import type {
  ActivityCounts,
  Course,
  CreateCourseRequest,
  CreateExperimentRequestBody,
  CreateReservationRequest,
  CreateShowcaseRequest,
  Enrollment,
  ExperimentRequest,
  Holiday,
  JobSummary,
  Lab,
  LabRequest,
  MigrationBatch,
  NotificationItem,
  PageQuery,
  Paginated,
  Period,
  PublicOverview,
  RegistrationItem,
  RejectReasonType,
  Reservation,
  Showcase,
  UnreadCount,
} from '../types/api';

export const labsApi = {
  list: async (q: PageQuery & Record<string, string | number | undefined> = {}) =>
    asPage<Lab>(await apiFetch(`/labs${qs(q)}`)),
  create: (body: LabRequest) =>
    apiFetch<Lab>('/labs', { method: 'POST', body: JSON.stringify(body) }),
  patch: (id: string, body: Partial<LabRequest>) =>
    apiFetch<Lab>(`/labs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
};

export const periodsApi = {
  list: async () => {
    const data = await apiFetch<Period[] | Paginated<Period>>('/periods');
    return Array.isArray(data) ? data : asPage<Period>(data).items;
  },
  create: (body: { name: string; sort_no?: number; starts_local: string; ends_local: string }) =>
    apiFetch<Period>('/periods', { method: 'POST', body: JSON.stringify(body) }),
};

export const holidaysApi = {
  list: async (year?: number) =>
    asPage<Holiday>(await apiFetch(`/holidays${qs({ year, page: 1, limit: 100 })}`)),
  create: (body: { date: string; name: string }) =>
    apiFetch<Holiday>('/holidays', { method: 'POST', body: JSON.stringify(body) }),
};

export const reservationsApi = {
  list: async (q: PageQuery & Record<string, string | number | undefined> = {}) =>
    asPage<Reservation>(await apiFetch(`/reservations${qs(q)}`)),
  create: (body: CreateReservationRequest) =>
    apiFetch<Reservation>('/reservations', { method: 'POST', body: JSON.stringify(body) }),
  approve: (id: string) =>
    apiFetch<Reservation>(`/reservations/${id}/approve`, { method: 'POST' }),
  reject: (id: string, body: { reason?: string; comment?: string }) =>
    apiFetch<Reservation>(`/reservations/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export const requestsApi = {
  list: async (q: PageQuery & Record<string, string | number | boolean | undefined> = {}) =>
    asPage<ExperimentRequest>(await apiFetch(`/experiment-requests${qs(q)}`)),
  create: (body: CreateExperimentRequestBody) =>
    apiFetch<ExperimentRequest>('/experiment-requests', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  approve: (id: string) =>
    apiFetch<ExperimentRequest>(`/experiment-requests/${id}/approve`, { method: 'POST' }),
  reject: (id: string, reason_code: RejectReasonType, reason_detail?: string) =>
    apiFetch<ExperimentRequest>(`/experiment-requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason_code, reason_detail: reason_detail || undefined }),
    }),
  withdraw: (id: string) =>
    apiFetch<ExperimentRequest>(`/experiment-requests/${id}/withdraw`, { method: 'POST' }),
};

export const coursesApi = {
  list: async (q: PageQuery & Record<string, string | number | undefined> = {}) =>
    asPage<Course>(await apiFetch(`/courses${qs(q)}`)),
  create: (body: CreateCourseRequest) =>
    apiFetch<Course>('/courses', { method: 'POST', body: JSON.stringify(body) }),
  publish: (id: string, client_token: string) =>
    apiFetch<Course>(`/courses/${id}/publish`, {
      method: 'POST',
      body: JSON.stringify({ client_token }),
    }),
  enroll: (id: string, client_token: string) =>
    apiFetch<Enrollment>(`/courses/${id}/enrollments`, {
      method: 'POST',
      body: JSON.stringify({ client_token }),
    }),
  enrollments: async (id: string) =>
    asPage<Enrollment>(await apiFetch(`/courses/${id}/enrollments${qs({ page: 1, limit: 100 })}`)),
  cancelEnrollment: (courseId: string, enrollmentId: string) =>
    apiFetch(`/courses/${courseId}/enrollments/${enrollmentId}`, { method: 'DELETE' }),
};

export const showcasesApi = {
  list: async (q: PageQuery & Record<string, string | number | undefined> = {}) =>
    asPage<Showcase>(await apiFetch(`/showcases${qs(q)}`)),
  create: (body: CreateShowcaseRequest) =>
    apiFetch<Showcase>('/showcases', { method: 'POST', body: JSON.stringify(body) }),
  publish: (id: string) => apiFetch(`/showcases/${id}/publish`, { method: 'POST' }),
  unpublish: (id: string) => apiFetch(`/showcases/${id}/unpublish`, { method: 'POST' }),
};

export const notificationsApi = {
  list: async () => asPage<NotificationItem>(await apiFetch('/notifications?page=1&limit=20')),
  unread: () => apiFetch<UnreadCount>('/notifications/unread-count'),
  read: (id: string) => apiFetch(`/notifications/${id}/read`, { method: 'POST' }),
};

export const jobsApi = {
  list: async () => {
    const data = await apiFetch<JobSummary[] | { items: JobSummary[] }>('/jobs');
    return Array.isArray(data) ? data : (data.items ?? []);
  },
};

export const statsApi = {
  counts: () => apiFetch<ActivityCounts>('/stats/activity-counts'),
  correct: (body: { from_value: number; to_value: number; reason: string }) =>
    apiFetch('/stats/activity-counts/corrections', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export const migrationsApi = {
  list: async () => {
    const data = await apiFetch<MigrationBatch[] | { items: MigrationBatch[] }>(
      '/migrations/batches',
    );
    return Array.isArray(data) ? data : (data.items ?? []);
  },
  get: (id: string) => apiFetch<MigrationBatch>(`/migrations/batches/${id}`),
  create: (body: { kind: string; rows: object[] }) =>
    apiFetch<MigrationBatch>('/migrations/batches', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  review: (id: string) => apiFetch(`/migrations/batches/${id}/review`, { method: 'POST' }),
  commit: (id: string) => apiFetch(`/migrations/batches/${id}/commit`, { method: 'POST' }),
  cutover: (id: string) => apiFetch(`/migrations/batches/${id}/cutover`, { method: 'POST' }),
};

export const usersApi = {
  import: (rows: { principal: string; name: string; role: string }[]) =>
    apiFetch('/users/import', { method: 'POST', body: JSON.stringify({ rows }) }),
  /** 教师代学生提交时按学号/姓名搜索，仅返回 id / 学号 / 姓名 */
  searchStudents: (q: string) =>
    apiFetch<{ id: string; principal: string; name: string }[]>(
      `/users/students/search${qs({ q })}`,
    ),
  registrations: async (q: { status?: string; role?: string; page?: number; limit?: number } = {}) =>
    asPage<RegistrationItem>(await apiFetch(`/users/registrations${qs(q)}`)),
  approveRegistration: (id: string) =>
    apiFetch<RegistrationItem>(`/users/registrations/${id}/approve`, { method: 'POST' }),
  rejectRegistration: (id: string, reason: string) =>
    apiFetch<RegistrationItem>(`/users/registrations/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};

export const publicApi = {
  overview: () => apiFetch<PublicOverview>('/public/overview'),
};

export async function exportExcel(resource: string) {
  const res = await apiFetch<Response>(`/exports/${resource}`, { raw: true });
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${resource}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
